import {
  Box,
  Button,
  FormHelperText,
  makeStyles,
  MenuItem,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@material-ui/core';
import axios from 'axios';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import PasswordTextField from '../atoms/PasswordTextField';
import {
  CnpjValidation,
  EmailValidation,
  LastNameValidation,
  LengthValidation,
  NameValidation,
  ObjectValidation,
  PasswordValidation,
  PhoneValidation,
} from '../functions/FormValidation';
import { SegmentOptions } from '../functions/Options';
import MaskedTextField from '../molecules/MaskedTextField';
import AutocompleteCityUF from './AutocompleteCityUF';
import TextFieldStatus from '../atoms/TextFieldStatus';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',

    '& .MuiStepper-root': {
      padding: '20px 0 0',
    },
    '& .MuiFormControl-root': {
      width: '70%',

      '@media screen and (max-width: 959px)': {
        width: '100%',
      },
    },
    '& .MuiFormHelperText-root': {
      color: theme.palette.error.main,
      marginBottom: '20px',
    },
  },
  inputWrap: {
    display: 'grid',
    marginBottom: '30px',
    gap: '15px',
  },
  submitButtons: {
    display: 'flex',
  },
}));

const renderStepLabel = (text) => (
  <StepLabel>
    <Typography variant="body1">{text}</Typography>
  </StepLabel>
);

const SwitchAccountantFormDrawer = ({
  data, setData, isLoading, setIsLoading,
}) => {
  const classes = useStyles();
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const name = 'Trocar Contador | Lead Drawer';

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const validateFields = () => (
    ObjectValidation(data, 9)
    && LengthValidation(data)
    && NameValidation(data)
    && LastNameValidation(data)
    && PhoneValidation(data)
    && EmailValidation(data)
    && CnpjValidation(data)
    && PasswordValidation(data)
  );

  const isFormValid = () => {
    try {
      return validateFields();
    } catch (e) {
      return false;
    }
  };

  const validateForm = () => {
    try {
      if (validateFields()) {
        setError({});
      }
    } catch (e) {
      setError({ field: e.fileName, message: e.message });
      return false;
    }
    return true;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (validateForm()) {
      setIsLoading(true);

      const registerData = {
        email: data.email,
        nome: data.firstname,
        senha: data.password,
        telefone: data.phone,
      };

      axios
        .post(`${process.env.GATSBY_API_BASE_URL}/api/migrar-empresa/novo-usuario`, registerData)
        .then((res) => {
          if (res.status === 200) {
            const { access_token: accessToken, refresh_token: refreshToken } = res.data;
            window.location = `${process.env.GATSBY_MIGRAR_EMPRESA_BASE_URL}/login/site/${accessToken}/${refreshToken}`;
          }
        })
        .catch((e) => {
          const errorMessage = e.response.data;

          if (errorMessage === 'Uma conta com este email já existe.') {
            setError({ field: 'email', message: 'Já existe uma conta registrada com esse endereço de e-mail.' });
          } else {
            setError({ field: '', message: e && e.reponse && e.response.data && e.response.data });
          }

          setIsLoading(false);
        });
    }
  };

  const changeData = (value, field) => {
    setData({ ...data, [field]: value });
    setError({});
  };

  const handleCityUFChange = (value) => {
    if (value) {
      setData({
        ...data,
        state: value.stateInitial,
        city: value.code,
        cityName: value.name,
      });
    } else {
      setData({
        ...data,
        state: null,
        city: null,
        cityName: null,
      });
    }
    setError({});
  };

  const renderButtons = () => (
    <div>
      <Button
        color="primary"
        onClick={handleBack}
        disabled={activeStep === 0 || isLoading}
      >
        Voltar
      </Button>
      <Button
        color="primary"
        variant="contained"
        onClick={handleNext}
        disabled={isLoading}
      >
        Avançar
      </Button>
    </div>
  );

  return (
    <div className={classes.root}>
      <form
        name={name}
        onSubmit={handleSubmit}
        className={process.env.GATSBY_CLASS_CADASTRO_TROCAR_CONTADOR}
      >
        <Typography variant="body1">Trocar de contador na Conube é rápido e online.</Typography>

        <Stepper activeStep={activeStep} orientation="vertical">
          <Step>
            {renderStepLabel('Informações pessoais')}

            <StepContent>
              <Box className={classes.inputWrap}>
                <TextField
                  label="Nome"
                  name="firstname"
                  value={data.firstname}
                  onChange={(e) => changeData(e.target.value, 'firstname')}
                  error={error.field === 'firstname'}
                  disabled={isLoading}
                />
                <TextField
                  label="Sobrenome"
                  name="lastName"
                  value={data.lastName}
                  onChange={(e) => changeData(e.target.value, 'lastName')}
                  error={error.field === 'lastName'}
                  disabled={isLoading}
                />
                <MaskedTextField
                  onChange={(e) => changeData(e.target.value, 'phone')}
                  mask="+55 (99) 99999-9999"
                  textFieldProps={{
                    label: 'Telefone',
                    name: 'phone',
                    type: 'tel',
                    value: data.phone,
                    floating: true,
                    error: error.field === 'phone',
                  }}
                  InputProps={{
                    disabled: isLoading,
                  }}
                />
              </Box>

              {renderButtons()}
            </StepContent>
          </Step>

          <Step>
            {renderStepLabel('Informações da sua empresa')}

            <StepContent>
              <Box className={classes.inputWrap}>
                <TextField
                  label="Qual segmento de atuação?"
                  name="segmento_atuacao"
                  value={data.segment}
                  onChange={(e) => changeData(e.target.value, 'segment')}
                  options={SegmentOptions}
                  disabled={isLoading}
                  select
                >
                  {SegmentOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <AutocompleteCityUF
                  onChange={(value) => handleCityUFChange(value)}
                  value={{
                    stateInitial: data.state,
                    code: data.city,
                    name: data.cityName,
                  }}
                  InputProps={{
                    endAdornment: (
                      <TextFieldStatus
                        error={Boolean(error.city)}
                        value={data.city && !error.city}
                      />
                    ),
                  }}
                  disabled={isLoading}
                />
                <MaskedTextField
                  onChange={(e) => changeData(e.target.value, 'cnpj')}
                  mask="99.999.999/9999-99"
                  textFieldProps={{
                    label: 'CNPJ',
                    name: 'cnpj',
                    type: 'text',
                    value: data.cnpj,
                    floating: true,
                    error: error.field === 'cnpj',
                  }}
                  InputProps={{
                    disabled: isLoading,
                  }}
                />
              </Box>

              {renderButtons()}
            </StepContent>
          </Step>

          <Step>
            {renderStepLabel('Cadastro')}

            <StepContent>
              <Box className={classes.inputWrap}>
                <TextField
                  label="E-mail"
                  name="email"
                  value={data.email}
                  onChange={(e) => changeData(e.target.value, 'email')}
                  error={error.field === 'email'}
                  disabled={isLoading}
                />
                <PasswordTextField
                  label="Senha"
                  name="password"
                  value={data.password}
                  onChange={(e) => changeData(e.target.value, 'password')}
                  error={error.field === 'password'}
                  disabled={isLoading}
                />
              </Box>

              <FormHelperText>{error.message}</FormHelperText>

              <div className={classes.submitButtons}>
                <Button
                  onClick={handleBack}
                  color="primary"
                  disabled={activeStep === 0 || isLoading}
                >
                  Voltar
                </Button>
              </div>
            </StepContent>
          </Step>
        </Stepper>
      </form>
    </div>
  );
};

SwitchAccountantFormDrawer.propTypes = {
  data: PropTypes.shape().isRequired,
  setData: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default SwitchAccountantFormDrawer;
